import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { YStack } from 'tamagui';
import { useMissingRequiredIndicator } from '../hooks/useMissingRequiredIndicator';
import type { ReportFormData, FormLayout } from '../reportCreation.types';
import { flattenFormLayoutSections } from '../reportCreation.utils';
import { FormRenderer } from './FormRenderer';
import { MissingFieldsIndicator } from './MissingFieldsIndicator';

interface ReportCreationStepContentProps {
  layout: FormLayout;
  showMissingIndicator: boolean;
  indicatorLabel: string;
}

export const ReportCreationStepContent = ({
  layout,
  showMissingIndicator,
  indicatorLabel,
}: ReportCreationStepContentProps): React.ReactElement => {
  const { control } = useFormContext<ReportFormData>();

  const repeatableSectionIds = useMemo(
    () => layout.repeatableSections?.map((section) => section.id) ?? [],
    [layout.repeatableSections],
  );

  const repeatableValues = useWatch({
    control,
    name: repeatableSectionIds,
  });

  const repeatableCounts = useMemo(
    () =>
      repeatableSectionIds.reduce<Record<string, number>>((acc, id, index) => {
        const current = repeatableValues?.[index];
        const currentCount = Array.isArray(current) ? current.length : 0;
        const minInstances =
          layout.repeatableSections?.find((section) => section.id === id)
            ?.minInstances ?? 0;

        return {
          ...acc,
          [id]: Math.max(currentCount, minInstances),
        };
      }, {}),
    [layout.repeatableSections, repeatableSectionIds, repeatableValues],
  );

  const flattenedSections = useMemo(
    () => flattenFormLayoutSections(layout, repeatableCounts),
    [layout, repeatableCounts],
  );

  const hasMissingRequiredFields = useMissingRequiredIndicator({
    sections: flattenedSections,
    control,
    enabled: showMissingIndicator,
  });

  return (
    <YStack gap="$3">
      {showMissingIndicator && hasMissingRequiredFields && (
        <MissingFieldsIndicator label={indicatorLabel} />
      )}
      <FormRenderer
        sections={layout.sections}
        repeatableSections={layout.repeatableSections}
      />
    </YStack>
  );
};
